import { ReactElement, ReactNode, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import {
  MenuDrawerContext,
  MenuDrawerDisplayType,
} from "../../contexts/MenuDrawerContext";

const getDisplayTypeFromMedia = (
  widthLessThan640: boolean,
  widthLessThan1280: boolean,
  heightLessThan428: boolean,
): MenuDrawerDisplayType => {
  if (heightLessThan428 || widthLessThan640) return "hidden";
  if (widthLessThan1280) return "rail";
  return "sticky";
};

export const MenuDrawerContextProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const widthLessThan640 = useMediaQuery("(max-width: 640px)");
  const widthLessThan1280 = useMediaQuery("(max-width: 1280px)");
  const heightLessThan428 = useMediaQuery("(max-height: 428px)");

  const mediaDisplayType = getDisplayTypeFromMedia(
    widthLessThan640,
    widthLessThan1280,
    heightLessThan428,
  );

  const [displayType, setDisplayType] =
    useState<MenuDrawerDisplayType>(mediaDisplayType);
  const [prevMediaDisplayType, setPrevMediaDisplayType] =
    useState(mediaDisplayType);

  const [displayTypeOverride, setDisplayTypeOverride] =
    useState<MenuDrawerDisplayType>();

  const [isOnScreen, setIsOnScreen] = useState(mediaDisplayType !== "hidden");
  const [prevOnScreenKey, setPrevOnScreenKey] = useState(
    `${displayType}:${displayTypeOverride ?? ""}`,
  );

  const [subMenuIndex, setSubMenuIndex] = useState<number>();

  // Re-apply media-driven display type when breakpoints change.
  if (mediaDisplayType !== prevMediaDisplayType) {
    setPrevMediaDisplayType(mediaDisplayType);
    setDisplayType(mediaDisplayType);
    if (mediaDisplayType === "hidden") {
      setIsOnScreen(false);
    }
  }

  // Keep isOnScreen in sync with display type / override (same rules as before).
  const onScreenKey = `${displayType}:${displayTypeOverride ?? ""}`;
  if (onScreenKey !== prevOnScreenKey) {
    setPrevOnScreenKey(onScreenKey);
    if (displayTypeOverride === "hidden") {
      setIsOnScreen(false);
    } else if (displayType !== "hidden") {
      setIsOnScreen(true);
    }
  }

  return (
    <MenuDrawerContext.Provider
      value={{
        displayType: displayTypeOverride ?? displayType,
        setDisplayType,
        displayTypeOverride,
        setDisplayTypeOverride,
        isOnScreen,
        setIsOnScreen: (nextIsOnScreen) => {
          if (displayType === "hidden" || displayTypeOverride === "hidden")
            setIsOnScreen(nextIsOnScreen);
        },
        isMobileDisplayType:
          displayTypeOverride === "hidden" || displayType === "hidden",
        // A short desktop viewport also hides the sidebar, but it still needs
        // desktop shell sizing and sticky navigation behavior.
        isMobileViewport: widthLessThan640,
        isRailDisplayType:
          displayTypeOverride === "rail" || displayType === "rail",
        isStickyDisplayType:
          displayTypeOverride === "sticky" || displayType === "sticky",
        subMenuIndex,
        setSubMenuIndex,
      }}
    >
      {children}
    </MenuDrawerContext.Provider>
  );
};
